import {
  type ChatMessage,
  type PeerRecord,
  type UserProfile,
  mergeChatMessageState,
  normalizeMessageFileInfo,
  sortMessages
} from '@skychat/shared-core';
import { isChatP2PFileMessage } from '../../../utils/chatP2PFiles';

export function parseJsonValue(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || !/^[{[]/.test(trimmed)) return value;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return value;
  }
}

export type DeliveryStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
export function encryptedDirectOutbound(message: ChatMessage, iv: string, payload: unknown): ChatMessage {
  const outbound: ChatMessage = { ...message, iv, payload };
  delete outbound.plain;
  return outbound;
}

export function normalizeDeliveryStatus(value: unknown, fallback?: DeliveryStatus): DeliveryStatus | undefined {
  const status = String(value || '').toLowerCase().trim();
  if (status === 'sending' || status === 'pending' || status === 'queued') return 'sending';
  if (status === 'sent' || status === 'ack' || status === 'accepted' || status === 'server') return 'sent';
  if (status === 'delivered' || status === 'received') return 'delivered';
  if (status === 'read' || status === 'seen') return 'read';
  if (status === 'failed' || status === 'error' || status === 'rejected') return 'failed';
  return fallback;
}

export function normalizeReadBy(value: unknown): string[] {
  const parsed = parseJsonValue(value);
  if (Array.isArray(parsed)) {
    return [...new Set(parsed.map((item) => String(item || '').trim()).filter(Boolean))];
  }
  if (parsed && typeof parsed === 'object') {
    return [...new Set(Object.entries(parsed as Record<string, unknown>)
      .filter(([, enabled]) => enabled !== false && enabled !== 0 && enabled !== '0')
      .map(([id]) => id.trim())
      .filter(Boolean))];
  }
  const id = String(parsed || '').trim();
  return id ? [id] : [];
}

export function mergeReadBy(current: unknown, next?: string): string[] | undefined {
  const ids = normalizeReadBy(current);
  if (next) ids.push(next);
  const unique = [...new Set(ids.filter(Boolean))];
  return unique.length ? unique : undefined;
}

export function safeReadStatus(status: unknown, readerId: string, currentMeId = '', fallback: DeliveryStatus = 'sent'): DeliveryStatus {
  const normalized = normalizeDeliveryStatus(status, fallback) || fallback;

  // The current server can emit "delivered" when the message has been accepted/synced
  // by the server, not when the remote device has actually received it. Treat that as
  // "sent" in the client so offline contacts do not immediately show a double check.
  if (normalized === 'delivered') return 'sent';

  if (normalized !== 'read') return normalized;
  return readerId && readerId !== currentMeId ? 'read' : fallback;
}

export function normalizeChatMessageForClient(input: ChatMessage): ChatMessage {
  const raw = input as ChatMessage & {
    edited?: unknown;
    fileInfo?: unknown;
    meta_json?: unknown;
    meta?: unknown;
    replyTo?: unknown;
  };
  const { fileInfo: _fileInfo, meta_json: _metaJson, ...rest } = raw;
  const fileInfo = parseJsonValue(_fileInfo);
  const meta = parseJsonValue(raw.meta ?? _metaJson);
  const replyTo = parseJsonValue(raw.replyTo);
  const payload = parseJsonValue(raw.payload);
  const readBy = normalizeReadBy((raw as ChatMessage & { readBy?: unknown }).readBy);
  const status = normalizeDeliveryStatus((raw as ChatMessage & { status?: unknown }).status);
  const file = normalizeMessageFileInfo(
    raw.file || (fileInfo && typeof fileInfo === 'object' ? fileInfo as Record<string, unknown> : undefined)
  );
  const ts = Number(raw.ts || 0);
  const seq = Number(raw.seq);
  const editedRaw = (input as unknown as { edited?: unknown }).edited;
  const editedTs = raw.editedTs == null ? raw.editedTs : Number(raw.editedTs);
  const normalized: ChatMessage = {
    ...rest,
    ts: Number.isFinite(ts) ? ts : Date.now(),
    payload,
    edited: editedRaw === true || editedRaw === 1 || editedRaw === '1',
    editedTs: Number.isFinite(Number(editedTs)) ? Number(editedTs) : raw.editedTs ?? null
  };

  if (Number.isFinite(seq)) normalized.seq = seq;
  if (status) normalized.status = status;
  else delete normalized.status;
  if (readBy.length) (normalized as ChatMessage & { readBy?: string[] }).readBy = readBy;
  else delete (normalized as ChatMessage & { readBy?: unknown }).readBy;
  if (file) normalized.file = file;
  else delete normalized.file;
  if (meta && typeof meta === 'object') normalized.meta = meta as ChatMessage['meta'];
  else delete normalized.meta;
  if (replyTo && typeof replyTo === 'object') normalized.replyTo = replyTo as ChatMessage['replyTo'];
  else delete normalized.replyTo;
  if (!normalized.edited) delete normalized.edited;
  if (!normalized.editedTs) delete normalized.editedTs;

  return normalized;
}

export function profileTextValue(source: Record<string, unknown> | null | undefined, keys: string[]) {
  if (!source) return '';
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const value = source[key];
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
}

export function profileHasAnyKey(source: Record<string, unknown> | null | undefined, keys: string[]) {
  return !!source && keys.some((key) => Object.prototype.hasOwnProperty.call(source, key));
}

export function createProfileUpdateSystemMessage(
  userId: string,
  previousPeer: PeerRecord | undefined,
  patch: Partial<UserProfile>,
  ownUserId: string
): ChatMessage | null {
  if (!userId || !previousPeer || previousPeer.type === 'group' || !ownUserId) return null;
  const previous = previousPeer as Record<string, unknown>;
  const next = { ...previous, ...(patch as Record<string, unknown>) };
  const patchRecord = patch as Record<string, unknown>;
  const oldNickname = profileTextValue(previous, ['nickname', 'name']);
  const newNickname = profileTextValue(next, ['nickname', 'name']) || oldNickname || userId;
  const oldAvatar = profileTextValue(previous, ['avatar_url', 'avatarUrl']);
  const newAvatar = profileTextValue(next, ['avatar_url', 'avatarUrl']);
  const nicknameChanged = profileHasAnyKey(patchRecord, ['nickname', 'name']) && !!newNickname && oldNickname !== newNickname;
  const avatarChanged = profileHasAnyKey(patchRecord, ['avatar_url', 'avatarUrl']) && oldAvatar !== newAvatar;
  if (!nicknameChanged && !avatarChanged) return null;
  const changeKind = nicknameChanged && avatarChanged ? 'profile' : avatarChanged ? 'avatar' : 'nickname';
  const now = Date.now();
  return {
    id: `local-profile-update:${userId}:${now}:${changeKind}`,
    fromId: userId,
    toId: ownUserId,
    ts: now,
    type: 'system_profile_update',
    plain: 'profile_update',
    meta: {
      systemKind: 'profile_update',
      changeKind,
      peerId: userId,
      peerNickname: newNickname,
      previousNickname: oldNickname,
      newNickname,
      avatarUrl: newAvatar
    }
  };
}

export function syncedMessagesFromResult(result: Record<string, unknown>): ChatMessage[] {
  const data = result.data;
  const candidates = [
    result.missedMessages,
    result.messages,
    Array.isArray(data) ? data : undefined,
    data && typeof data === 'object' ? (data as Record<string, unknown>).missedMessages : undefined,
    data && typeof data === 'object' ? (data as Record<string, unknown>).messages : undefined
  ];
  const raw = candidates.find(Array.isArray) as ChatMessage[] | undefined;
  return (raw || []).map(normalizeChatMessageForClient);
}

export function profileFromResult(result: Record<string, unknown> | null | undefined): UserProfile | null {
  const candidates = [
    result?.profile,
    result?.me,
    result?.user,
    result?.data && typeof result.data === 'object' ? (result.data as Record<string, unknown>).profile : undefined,
    result?.data && typeof result.data === 'object' ? (result.data as Record<string, unknown>).me : undefined,
    result?.data && typeof result.data === 'object' ? (result.data as Record<string, unknown>).user : undefined
  ];
  const profile = candidates.find((item) => item && typeof item === 'object' && typeof (item as UserProfile).id === 'string') as UserProfile | undefined;
  return profile || null;
}

export class AuthSessionExpiredError extends Error {
  constructor(message = 'Authentication session expired') {
    super(message);
    this.name = 'AuthSessionExpiredError';
  }
}

export function isAuthFailureResult(result: Record<string, unknown> | null | undefined): boolean {
  const status = Number(result?.status || result?.httpStatus || result?.code || 0);
  if (status === 401 || status === 403) return true;
  const error = String(result?.error || result?.message || '').toLowerCase();
  return error.includes('no token') ||
    error.includes('bad token') ||
    error.includes('invalid token') ||
    error.includes('jwt') ||
    error.includes('unauthorized') ||
    error.includes('forbidden') ||
    error.includes('not authenticated');
}

export function clearLegacyAuthStorage(): void {
  try { localStorage.removeItem('skychat_token'); } catch { }
  try { sessionStorage.removeItem('skychat_token'); } catch { }
}

export function mergeMessageLists(...lists: ChatMessage[][]): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  lists.flat().forEach((message) => {
    const normalized = normalizeChatP2PMessageForCache(message);
    const existing = byId.get(message.id);
    byId.set(message.id, existing ? mergeChatMessages(existing, normalized) : normalized);
  });
  return sortMessages([...byId.values()]);
}

export function mergeHydratedMessageLists(...lists: ChatMessage[][]): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  lists.flat().forEach((message) => {
    const normalized = normalizeChatP2PMessageForCache(message);
    const existing = byId.get(message.id);
    if (!existing) {
      byId.set(message.id, normalized);
      return;
    }
    byId.set(
      message.id,
      isChatP2PFileMessage(existing) || isChatP2PFileMessage(normalized)
        ? mergeChatMessages(existing, normalized)
        : mergeChatMessageState(existing, normalized)
    );
  });
  return sortMessages([...byId.values()]);
}

export function chatMessageMeta(message: ChatMessage): Record<string, unknown> {
  const raw = (message as ChatMessage & { meta?: unknown; meta_json?: unknown }).meta
    ?? (message as ChatMessage & { meta_json?: unknown }).meta_json;
  const parsed = parseJsonValue(raw);
  return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
}

export function isChatP2PSuccessMessage(message: ChatMessage): boolean {
  if (!isChatP2PFileMessage(message)) return false;
  const status = String(message.status || '').toLowerCase();
  const phase = String(chatMessageMeta(message).phase || '').toLowerCase();
  return status === 'sent' || status === 'delivered' || status === 'read' || phase === 'done';
}

export function isChatP2PTemporaryMessage(message: ChatMessage): boolean {
  if (!isChatP2PFileMessage(message)) return false;
  const status = String(message.status || '').toLowerCase();
  const phase = String(chatMessageMeta(message).phase || '').toLowerCase();
  return status === 'sending' || status === 'failed' ||
    phase === 'waiting' || phase === 'connecting' || phase === 'transferring' || phase === 'finalizing' || phase === 'failed';
}

export function normalizeChatP2PMessageForCache(message: ChatMessage): ChatMessage {
  if (!isChatP2PFileMessage(message)) return message;
  let changed = false;
  let next = message;
  if (message.file && String(message.file.id || '').startsWith('p2p-') && message.file.url !== '') {
    next = { ...next, file: { ...message.file, url: '' } };
    changed = true;
  }
  if (isChatP2PSuccessMessage(next)) {
    const meta = chatMessageMeta(next);
    const status = String(next.status || '').toLowerCase();
    next = {
      ...next,
      status: status === 'sent' || status === 'delivered' || status === 'read' ? next.status : 'sent',
      uploadProgress: 100,
      meta: meta.phase === 'done' ? meta : { ...meta, phase: 'done' }
    } as ChatMessage & Record<string, unknown>;
    delete (next as ChatMessage & { error?: unknown }).error;
    changed = true;
  }
  return changed ? next : message;
}

export function mergeChatMessages(existing: ChatMessage, incoming: ChatMessage): ChatMessage {
  if (!isChatP2PFileMessage(existing) && !isChatP2PFileMessage(incoming)) return { ...existing, ...incoming };
  if (isChatP2PSuccessMessage(existing) && isChatP2PTemporaryMessage(incoming)) {
    return normalizeChatP2PMessageForCache(existing);
  }
  return normalizeChatP2PMessageForCache({
    ...existing,
    ...incoming,
    meta: {
      ...chatMessageMeta(existing),
      ...chatMessageMeta(incoming)
    }
  } as ChatMessage);
}

export function messageCountsForSyncCursor(message: ChatMessage): boolean {
  return !isChatP2PTemporaryMessage(message);
}

export function maxMessageTs(messages: ChatMessage[]): number {
  return Math.max(0, ...messages.map((message) => Number(message.ts || 0)).filter(Number.isFinite));
}

export function maxMessageSeq(messages: ChatMessage[]): number {
  return Math.max(0, ...messages.map((message) => Number(message.seq || 0)).filter(Number.isFinite));
}

export function messageNeedsHydration(message: ChatMessage): boolean {
  return !message.plain && !!message.iv && !!message.payload && message.type !== 'deleted';
}

export function isAcceptedConversationPeer(peer: PeerRecord): boolean {
  return !!peer.id && (peer.status === 'accepted' || peer.type === 'group');
}
